import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const BetaNotes = () => {
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteStatus, setNewNoteStatus] = useState('open');
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteStatus, setEditNoteStatus] = useState('open');
  const [betaMode, setBetaMode] = useState(false);

  // Check if beta mode is enabled
  useEffect(() => {
    const checkBetaMode = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/beta-mode`);
        setBetaMode(response.data.beta_mode || false);
      } catch (err) {
        console.error('Error checking beta mode:', err);
        setBetaMode(false);
      }
    };
    checkBetaMode();
  }, []);

  // Fetch notes when modal opens
  useEffect(() => {
    if (showModal) {
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, location.pathname]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/beta-notes`, {
        params: {
          page_path: location.pathname
        }
      });
      setNotes(response.data || []);
    } catch (err) {
      console.error('Error fetching beta notes:', err);
      alert('Error fetching notes: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/beta-notes`, {
        page_path: location.pathname,
        note_text: newNoteText,
        status: newNoteStatus
      });

      setNotes([response.data, ...notes]);
      setNewNoteText('');
      setNewNoteStatus('open');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error creating note:', err);
      alert('Error creating note: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateNote = async (note) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/beta-notes/${note.vuid}`, {
        note_text: editNoteText,
        status: editNoteStatus
      });

      setNotes(notes.map(n => n.vuid === note.vuid ? response.data : n));
      setEditingNote(null);
      setEditNoteText('');
      setEditNoteStatus('open');
    } catch (err) {
      console.error('Error updating note:', err);
      alert('Error updating note: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/beta-notes/${note.vuid}`);
      setNotes(notes.filter(n => n.vuid !== note.vuid));
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Error deleting note: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setEditNoteText(note.note_text);
    setEditNoteStatus(note.status);
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditNoteText('');
    setEditNoteStatus('open');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'defer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Don't render if beta mode is not enabled
  if (!betaMode) {
    return null;
  }

  return (
    <>
      {/* Beta Notes Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-2 rounded-md shadow-lg z-40 flex items-center gap-2"
        title="Beta Notes"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Beta Notes
      </button>

      {/* Beta Notes Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Beta Notes</h2>
                <p className="text-sm text-gray-600 mt-1">Page: {location.pathname}</p>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setShowAddForm(false);
                  setEditingNote(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Add Note Button */}
            <div className="mb-4">
              {!showAddForm ? (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  + Add Note
                </button>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Add New Note</h3>
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Enter your feedback, suggestions, or issues..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3"
                    rows="3"
                  />
                  <div className="flex items-center gap-4 mb-3">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    <select
                      value={newNoteStatus}
                      onChange={(e) => setNewNoteStatus(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="resolved">Resolved</option>
                      <option value="defer">Defer</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddNote}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewNoteText('');
                        setNewNoteStatus('open');
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notes Table */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading notes...</p>
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No notes yet. Click "Add Note" to create one.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Note
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notes.map((note) => (
                        <tr key={note.vuid}>
                          <td className="px-6 py-4 whitespace-normal">
                            {editingNote && editingNote.vuid === note.vuid ? (
                              <div>
                                <textarea
                                  value={editNoteText}
                                  onChange={(e) => setEditNoteText(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                  rows="3"
                                />
                                <div className="mt-2">
                                  <select
                                    value={editNoteStatus}
                                    onChange={(e) => setEditNoteStatus(e.target.value)}
                                    className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                  >
                                    <option value="open">Open</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="defer">Defer</option>
                                  </select>
                                </div>
                                <div className="mt-2 flex gap-2">
                                  <button
                                    onClick={() => handleUpdateNote(note)}
                                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-xs font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-900 whitespace-pre-wrap">{note.note_text}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {editingNote && editingNote.vuid === note.vuid ? null : (
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(note.status)}`}>
                                {note.status.charAt(0).toUpperCase() + note.status.slice(1)}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {note.created_by || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {note.created_at ? new Date(note.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {editingNote && editingNote.vuid === note.vuid ? null : (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(note)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteNote(note)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BetaNotes;
